import React from 'react'
// libs
import moment from 'moment'
import { useStore } from 'react-redux'
import { getFormValues } from 'redux-form'
import { useMutation, useQuery } from '@apollo/client/react'
// hooks
import { usePlanItemNotification } from 'hooks/usePlanItemNotification'
import { useStrategyCompletionProcess } from 'hooks/useStrategyCompletionProcess'
import { useApolloClient, useDefaultObjectSecurity, useModalData } from 'hooks'
// graphql
import { PLAN_ACTION_DETAILS_QUERY } from 'graphql/planActions/queries'
import { PLAN_ACTION_UPDATE_MUTATION } from 'graphql/planActions/mutations'
// constants
import { ENTITIES, Modals, Forms } from 'constants/index'
// types
import { NetworkStatus } from '@apollo/client'
import { IPlanAction, SystemProcessObject } from 'types/graphql'
// context
import { ActionContext } from 'context'
// lodash
import get from 'lodash/get'
import round from 'lodash/fp/round'
import defaultTo from 'lodash/fp/defaultTo'
// utils
import {
  generateInitialOwnedByValue,
  getFieldsInitialValues,
  getFieldValues,
  getOptionsByEntity,
  getPlanFromEntity,
  getValueFromOption,
  getValuesFromOptions,
  parseGraphQLErrorMessages
} from 'utils'
import { runPendingUploads } from 'utils/pendingFileUploads'

interface Props {
  entity?: { is: string } & any
}

const withAction = (EnhancedComponent: React.FC<any>) => {
  const EnhancedWithAction: React.FC<Props> = (props) => {
    const { client } = useApolloClient()

    const store = useStore()

    const { modalData } = useModalData({ modalType: Modals.ACTION_MINIMIZED_MODAL })

    const actionId = modalData?.id || props.entity?.id

    const {
      data: detailsQuery,
      refetch,
      networkStatus,
      loading
    } = useQuery(PLAN_ACTION_DETAILS_QUERY, {
      skip: !actionId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          ids: actionId
        }
      }
    })

    const [updateMutation] = useMutation(PLAN_ACTION_UPDATE_MUTATION)

    const runCompletionProcess = useStrategyCompletionProcess()

    // constants
    const entity: IPlanAction = get(detailsQuery, 'planActions.data[0]')

    usePlanItemNotification(entity?.id, refetch)

    const plan = getPlanFromEntity(entity)
    const isLoading = !entity && ((loading && networkStatus !== NetworkStatus.refetch) || (!!actionId && modalData.isOpen))
    const defaultObjectSecurities = useDefaultObjectSecurity({ objectApiName: 'actions', planType: plan.planType })

    // methods
    const getInitialValues = async () => {
      const fieldInitialValues = await getFieldsInitialValues({
        fieldValues: get(entity, 'fieldValueMap', []),
        fields: get(entity, 'fields', []),
        client
      })

      return {
        ...fieldInitialValues,
        completionPercentage: round(entity.completionPercentage),
        description: entity.description,
        ownedByEmployeeOrPosition: generateInitialOwnedByValue(entity),
        plan: plan ? { value: plan.id, label: plan.name } : null,
        commencementAt: entity.commencementAt,
        completionAt: entity.completionAt
      }
    }

    const update = async ({ variables }) => {
      await updateMutation({ variables })
    }

    const handleDetailsSubmit = async (formData) => {
      try {
        await runPendingUploads(Forms.ACTION_DETAILS_FORM)
        const data = getFormValues(Forms.ACTION_DETAILS_FORM)(store.getState()) || formData

        const prevPercentage = entity.completionPercentage
        const nextPercentage = defaultTo(0, data.completionPercentage)
        const isCompleting = prevPercentage < 100 && nextPercentage === 100

        const actualCompletionDateId = entity.fields.find((field) => field.name === 'actualCompletionDate')?.id
        const baseFieldValues = getFieldValues(entity.fields, data, defaultObjectSecurities)
        const fieldValues = isCompleting && actualCompletionDateId
          ? [...baseFieldValues.filter((fv) => fv?.fieldId !== actualCompletionDateId), { fieldId: actualCompletionDateId, value: moment().format('YYYY-MM-DD') }]
          : baseFieldValues

        await update({
          variables: {
            id: entity.id,
            input: {
              fieldValues,
              commencementAt: data.commencementAt,
              completionAt: data.completionAt,
              completionPercentage: nextPercentage,
              description: data.description,
              memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
              memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
              ownedById: getValueFromOption(data.ownedByEmployeeOrPosition)
            }
          }
        })

        await runCompletionProcess({
          entityId: entity.id,
          object: SystemProcessObject.Action,
          prev: prevPercentage,
          next: nextPercentage
        })
      } catch (errors) {
        console.log('[withAction.handleDetailsSubmit]: ', parseGraphQLErrorMessages(errors))
      }
    }

    if (!isLoading && !entity && !modalData.isOpen) {
      return null
    }

    const value = {
      plan,
      update,
      refetch,
      isLoading,
      getInitialValues,
      handleDetailsSubmit,
      entity: { ...entity, parentPermissions: get(modalData, 'parentPermissions') }
    }

    return (
      <ActionContext.Provider value={value}>
        <EnhancedComponent {...props} />
      </ActionContext.Provider>
    )
  }

  return EnhancedWithAction
}

export default withAction
